import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CalendarEventInput,
  ExerciseDetail,
  ExerciseInput,
  ExerciseRecord,
  CalendarEventRecord,
  PlayerInput,
  PlayerPatch,
  PlayerRecord,
  PositionRecord,
  PlayerStatusRecord,
  SeasonEditInput,
  SeasonInput,
  SeasonPhaseInput,
  TeamInput,
  TeamRecord,
} from '@shared/schemas';
import { ApiFailure, api, getActiveOrg } from '@/lib/api';
import { postEventStream } from '@/lib/sse';

/**
 * Query keys carry the org id. Switching team must not show another club's cache,
 * and a cache that ignores the tenant is the frontend version of an unscoped
 * WHERE clause.
 */
const key = {
  config: () => ['config', getActiveOrg()] as const,
  teams: () => ['teams', getActiveOrg()] as const,
  seasons: (teamId?: string) => ['seasons', getActiveOrg(), teamId ?? 'all'] as const,
  phases: (seasonId: string) => ['phases', getActiveOrg(), seasonId] as const,
  players: (teamId?: string) => ['players', getActiveOrg(), teamId ?? 'all'] as const,
  player: (playerId: string) => ['player', getActiveOrg(), playerId] as const,
  exerciseConfig: () => ['exercise-config', getActiveOrg()] as const,
  exercises: (search?: string, category?: string) =>
    ['exercises', getActiveOrg(), search ?? '', category ?? ''] as const,
  exercise: (id: string) => ['exercise', getActiveOrg(), id] as const,
  events: (teamId?: string, from?: string, to?: string) =>
    ['events', getActiveOrg(), teamId ?? 'all', from ?? '', to ?? ''] as const,
};

export interface SeasonRecord {
  id: string;
  teamId: string;
  label: string;
  startsOn: string;
  endsOn: string;
  status: string;
}

export interface PhaseRecord {
  id: string;
  seasonId: string;
  name: string;
  kind: string | null;
  startsOn: string;
  endsOn: string;
  objectiveSummary: string | null;
  sort: number;
}

export function useConfig(enabled = true) {
  return useQuery({
    queryKey: key.config(),
    enabled: enabled && Boolean(getActiveOrg()),
    // Positions and statuses change when a coach edits them, which is rare.
    staleTime: 10 * 60_000,
    queryFn: () =>
      api.get<{ positions: PositionRecord[]; statuses: PlayerStatusRecord[] }>('/config'),
  });
}

export function useTeams() {
  return useQuery({
    queryKey: key.teams(),
    enabled: Boolean(getActiveOrg()),
    queryFn: async () => (await api.get<{ teams: TeamRecord[] }>('/teams')).teams,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TeamInput) => api.post<{ id: string }>('/teams', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key.teams() }),
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, input }: { teamId: string; input: TeamInput }) =>
      api.put(`/teams/${teamId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key.teams() }),
  });
}

export function useArchiveTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => api.del(`/teams/${teamId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key.teams() });
      qc.invalidateQueries({ queryKey: ['players', getActiveOrg()] });
    },
  });
}

export function useSeasons(teamId?: string) {
  return useQuery({
    queryKey: key.seasons(teamId),
    enabled: Boolean(getActiveOrg()),
    queryFn: async () =>
      (
        await api.get<{ seasons: SeasonRecord[] }>(
          teamId ? `/seasons?teamId=${encodeURIComponent(teamId)}` : '/seasons',
        )
      ).seasons,
  });
}

export function useCreateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SeasonInput) => api.post<{ id: string }>('/seasons', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seasons', getActiveOrg()] }),
  });
}

export function useUpdateSeason(seasonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SeasonEditInput) => api.put(`/seasons/${seasonId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['seasons', getActiveOrg()] });
      qc.invalidateQueries({ queryKey: ['phases', getActiveOrg()] });
    },
  });
}

export function usePhases(seasonId: string | null) {
  return useQuery({
    queryKey: key.phases(seasonId ?? ''),
    enabled: Boolean(getActiveOrg()) && Boolean(seasonId),
    queryFn: async () =>
      (await api.get<{ phases: PhaseRecord[] }>(`/seasons/${seasonId}/phases`)).phases,
  });
}

export function useCreatePhase(seasonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SeasonPhaseInput) =>
      api.post<{ id: string }>(`/seasons/${seasonId}/phases`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key.phases(seasonId) }),
  });
}

export function useUpdatePhase(seasonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, input }: { phaseId: string; input: SeasonPhaseInput }) =>
      api.put(`/phases/${phaseId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key.phases(seasonId) }),
  });
}

export function useDeletePhase(seasonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseId: string) => api.del(`/phases/${phaseId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key.phases(seasonId) }),
  });
}

export function usePlayers(teamId?: string, archived = false) {
  return useQuery({
    queryKey: [...key.players(teamId), archived ? 'archived' : 'active'],
    enabled: Boolean(getActiveOrg()),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      if (archived) params.set('archived', '1');
      const query = params.toString();
      return (
        await api.get<{ players: PlayerRecord[] }>(`/players${query ? `?${query}` : ''}`)
      ).players;
    },
  });
}

/**
 * Removes players with nothing recorded about them, archives the rest.
 *
 * A squad imported from the wrong column has no history to protect; a player who
 * has trained and been rated has a season of it.
 */
export function useDeletePlayers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playerIds: string[]) =>
      api.post<{ deleted: number; kept: { playerId: string; history: string[] }[] }>(
        '/players/delete',
        { playerIds },
      ),
    onSuccess: () => invalidatePlayers(qc),
  });
}

function invalidatePlayers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['players', getActiveOrg()] });
  // A squad change moves a team's player count, so the team list is stale too.
  qc.invalidateQueries({ queryKey: key.teams() });
}

export function useCreatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlayerInput) => api.post<{ id: string }>('/players', input),
    onSuccess: () => invalidatePlayers(qc),
  });
}

export function useUpdatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, patch }: { playerId: string; patch: PlayerPatch }) =>
      api.patch(`/players/${playerId}`, patch),
    onSuccess: (_data, { playerId }) => {
      invalidatePlayers(qc);
      qc.invalidateQueries({ queryKey: key.player(playerId) });
    },
  });
}

export function useArchivePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playerId: string) => api.del(`/players/${playerId}`),
    onSuccess: () => invalidatePlayers(qc),
  });
}

export function useEvents(teamId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: key.events(teamId, from, to),
    enabled: Boolean(getActiveOrg()),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const query = params.toString();
      return (
        await api.get<{ events: CalendarEventRecord[] }>(
          `/events${query ? `?${query}` : ''}`,
        )
      ).events;
    },
  });
}

function invalidateEvents(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['events', getActiveOrg()] });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CalendarEventInput) => api.post<{ id: string }>('/events', input),
    onSuccess: () => invalidateEvents(qc),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, input }: { eventId: string; input: CalendarEventInput }) =>
      api.put(`/events/${eventId}`, input),
    onSuccess: () => invalidateEvents(qc),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api.del(`/events/${eventId}`),
    onSuccess: () => invalidateEvents(qc),
  });
}

export interface ExerciseConfig {
  categories: Array<{ key: string; label: string; i18nKey: string | null }>;
  systems: Array<{ key: string; label: string; i18nKey: string | null }>;
  qualities: Array<{ key: string; label: string; i18nKey: string | null; family: string | null }>;
}

export function useExerciseConfig() {
  return useQuery({
    queryKey: key.exerciseConfig(),
    enabled: Boolean(getActiveOrg()),
    staleTime: 10 * 60_000,
    queryFn: () => api.get<ExerciseConfig>('/exercises/config'),
  });
}

export function useExercises(search?: string, category?: string) {
  return useQuery({
    queryKey: key.exercises(search, category),
    enabled: Boolean(getActiveOrg()),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      const q = params.toString();
      return (await api.get<{ exercises: ExerciseRecord[] }>(`/exercises${q ? `?${q}` : ''}`))
        .exercises;
    },
  });
}

export function useExercise(exerciseId: string | null) {
  return useQuery({
    queryKey: key.exercise(exerciseId ?? ''),
    enabled: Boolean(getActiveOrg()) && Boolean(exerciseId),
    queryFn: () => api.get<ExerciseDetail>(`/exercises/${exerciseId}`),
  });
}

function invalidateExercises(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['exercises', getActiveOrg()] });
}

export function useCreateExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExerciseInput) => api.post<{ id: string }>('/exercises', input),
    onSuccess: () => invalidateExercises(qc),
  });
}

export function useUpdateExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ exerciseId, input }: { exerciseId: string; input: ExerciseInput }) =>
      api.put(`/exercises/${exerciseId}`, input),
    onSuccess: (_d, { exerciseId }) => {
      invalidateExercises(qc);
      qc.invalidateQueries({ queryKey: key.exercise(exerciseId) });
    },
  });
}

export function useDuplicateExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ exerciseId, name }: { exerciseId: string; name: string }) =>
      api.post<{ id: string }>(`/exercises/${exerciseId}/duplicate`, { name }),
    onSuccess: () => invalidateExercises(qc),
  });
}

export function useArchiveExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (exerciseId: string) => api.del(`/exercises/${exerciseId}`),
    onSuccess: () => invalidateExercises(qc),
  });
}

// ── Planning, sessions, execution ──────────────────────────────────────

import type {
  GameModelInput,
  MicrocycleInput,
  PrincipleInput,
  SessionActualInput,
  SessionEvaluationInput,
  SessionInput,
  SessionRecord,
} from '@shared/schemas';

export interface PrincipleRecord {
  id: string;
  phase: string;
  moment: string | null;
  name: string;
  description: string | null;
  sort: number;
}

export interface GameModelRecord {
  id: string;
  teamId: string;
  name: string;
  formation: string | null;
  philosophy: string | null;
  principles: PrincipleRecord[];
}

export interface MicrocycleDayRow {
  id: string;
  onDate: string;
  mdLabel: string | null;
  mainObjective: string | null;
  plannedLoad: number | null;
  plannedRpe: number | null;
  plannedDurationMin: number | null;
  energySystemKey: string | null;
  physicalEmphasis: string | null;
  tacticalEmphasis: string | null;
  dayType: string | null;
  notes: string | null;
}

export interface MicrocycleRow {
  id: string;
  teamId: string;
  name: string;
  startsOn: string;
  endsOn: string;
  anchorEventId: string | null;
  objectiveSummary: string | null;
  adjustmentReason: string | null;
  days: MicrocycleDayRow[];
}

export interface ReadinessRow {
  playerId: string;
  firstName: string;
  lastName: string;
  shirtNumber: number | null;
  status: 'fresh' | 'normal' | 'watch' | 'fatigued' | 'high_concern';
  ratio: number;
  signals: string[];
  insufficientHistory: boolean;
  acute7: number;
  chronicWeekly: number;
  /**
   * What the figure rests on, carried from the stored row.
   *
   * The weakest kind among the days the verdict used, never the commonest:
   * nine measured days and one estimate is not a measured week.
   */
  provenance: Provenance;
  provenanceCounts: Partial<Record<Provenance, number>>;
}

export type Provenance =
  | 'estimated_from_plan'
  | 'coach_observed'
  | 'derived'
  | 'player_reported'
  | 'measured';

export interface RegisterRow {
  playerId: string;
  firstName: string;
  lastName: string;
  shirtNumber: number | null;
  positionKey: string | null;
  status: string;
  minutesDone: number | null;
  note: string | null;
}

export function useGameModels(teamId?: string) {
  return useQuery({
    queryKey: ['game-models', getActiveOrg(), teamId ?? 'all'],
    enabled: Boolean(getActiveOrg()),
    queryFn: async () =>
      (
        await api.get<{ models: GameModelRecord[] }>(
          teamId ? `/game-models?teamId=${encodeURIComponent(teamId)}` : '/game-models',
        )
      ).models,
  });
}

export function useSaveGameModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ modelId, input }: { modelId?: string; input: GameModelInput }) =>
      modelId ? api.put(`/game-models/${modelId}`, input) : api.post('/game-models', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['game-models', getActiveOrg()] }),
  });
}

export function useAddPrinciple(modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PrincipleInput) =>
      api.post(`/game-models/${modelId}/principles`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['game-models', getActiveOrg()] }),
  });
}

export function useDeletePrinciple() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (principleId: string) => api.del(`/principles/${principleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['game-models', getActiveOrg()] }),
  });
}

export function useMicrocycles(teamId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['microcycles', getActiveOrg(), teamId ?? '', from ?? '', to ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: async () => {
      const params = new URLSearchParams({ teamId: teamId! });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      return (await api.get<{ microcycles: MicrocycleRow[] }>(`/microcycles?${params}`))
        .microcycles;
    },
  });
}

export function useSaveMicrocycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ microcycleId, input }: { microcycleId?: string; input: MicrocycleInput }) =>
      microcycleId
        ? api.put(`/microcycles/${microcycleId}`, input)
        : api.post('/microcycles', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['microcycles', getActiveOrg()] }),
  });
}

export function useSessions(teamId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['sessions', getActiveOrg(), teamId ?? '', from ?? '', to ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: async () => {
      const params = new URLSearchParams({ teamId: teamId! });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      return (await api.get<{ sessions: SessionRecord[] }>(`/sessions?${params}`)).sessions;
    },
  });
}

export function useSession(sessionId: string | null) {
  return useQuery({
    queryKey: ['session', getActiveOrg(), sessionId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(sessionId),
    queryFn: () => api.get<Record<string, unknown>>(`/sessions/${sessionId}`),
  });
}

function invalidateSessions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['sessions', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['session', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['load', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['readiness', getActiveOrg()] });
}

export function useSaveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, input }: { sessionId?: string; input: SessionInput }) =>
      sessionId ? api.put(`/sessions/${sessionId}`, input) : api.post('/sessions', input),
    onSuccess: () => invalidateSessions(qc),
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.del<{ deleted: boolean; history: string[] }>(`/sessions/${sessionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', getActiveOrg()] });
      qc.invalidateQueries({ queryKey: ['load', getActiveOrg()] });
      qc.invalidateQueries({ queryKey: ['microcycles', getActiveOrg()] });
    },
  });
}

export function useSessionRegister(sessionId: string | null) {
  return useQuery({
    queryKey: ['register', getActiveOrg(), sessionId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(sessionId),
    queryFn: () =>
      api.get<{
        session: SessionRecord;
        evaluation: SessionEvaluationInput | null;
        players: RegisterRow[];
      }>(`/sessions/${sessionId}/register`),
  });
}

export function useRecordActuals(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SessionActualInput) =>
      api.post<{ players: number }>(`/sessions/${sessionId}/actuals`, input),
    onSuccess: () => {
      invalidateSessions(qc);
      qc.invalidateQueries({ queryKey: ['register', getActiveOrg()] });
    },
  });
}

export function useSaveEvaluation(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SessionEvaluationInput) =>
      api.put(`/sessions/${sessionId}/evaluation`, input),
    onSuccess: () => invalidateSessions(qc),
  });
}

export function useReadiness(teamId?: string, onDate?: string) {
  return useQuery({
    queryKey: ['readiness', getActiveOrg(), teamId ?? '', onDate ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: async () => {
      const params = new URLSearchParams({ teamId: teamId! });
      if (onDate) params.set('onDate', onDate);
      return (await api.get<{ players: ReadinessRow[] }>(`/readiness?${params}`)).players;
    },
  });
}

export interface TeamLoad {
  sessions: Array<{
    id: string;
    onDate: string;
    plannedLoad: number | null;
    actualLoad: number | null;
    plannedRpe: number | null;
    actualRpe: number | null;
    plannedDurationMin: number | null;
    actualDurationMin: number | null;
    status: string;
    title: string | null;
    objective: string | null;
  }>;
  matches: Array<{ onDate: string; kind: string; opponent: string | null; status: string }>;
}

export function useTeamLoad(teamId?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['load', getActiveOrg(), teamId ?? '', from ?? '', to ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId) && Boolean(from) && Boolean(to),
    queryFn: () =>
      api.get<TeamLoad>(
        `/load?teamId=${encodeURIComponent(teamId!)}&from=${from}&to=${to}`,
      ),
  });
}

export interface PickableExercise {
  id: string;
  name: string;
  categoryKey: string | null;
  durationMin: number | null;
  intensity: number | null;
  workSeconds: number | null;
  restSeconds: number | null;
  sets: number | null;
  reps: number | null;
}

export function usePickableExercises() {
  return useQuery({
    queryKey: ['pickable', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    staleTime: 5 * 60_000,
    queryFn: async () =>
      (await api.get<{ exercises: PickableExercise[] }>('/session-exercises')).exercises,
  });
}

// ── Matches and objectives ─────────────────────────────────────────────

import type {
  MatchAnalysisInput,
  MatchDetailInput,
  MatchProblemInput,
  MatchAttendanceInput,
  MatchPlanInput,
  MatchRecord,
  MatchSquadEntry,
  ObjectiveInput,
  ObjectiveRecord,
  PromoteProblemInput,
} from '@shared/schemas';

export function useMatch(eventId: string | null) {
  return useQuery({
    queryKey: ['match', getActiveOrg(), eventId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(eventId),
    queryFn: () => api.get<MatchRecord>(`/matches/${eventId}`),
  });
}

function invalidateMatch(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['match', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['objectives', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['load', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['readiness', getActiveOrg()] });
}

export function useSaveMatchDetail(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchDetailInput) => api.put(`/matches/${eventId}/detail`, input),
    onSuccess: () => invalidateMatch(qc),
  });
}

export function useSaveMatchSquad(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entries: MatchSquadEntry[]; matchRpe?: number }) =>
      api.put(`/matches/${eventId}/squad`, input),
    onSuccess: () => invalidateMatch(qc),
  });
}

export interface MatchPlanRecord {
  periods: {
    id: string;
    fromMinute: number;
    toMinute: number;
    label: string | null;
    formation: string | null;
    note: string | null;
    players: { playerId: string; positionKey: string | null }[];
  }[];
  minutes: { playerId: string; minutes: number; spells: number }[];
  substitutions: { atMinute: number; off: string[]; on: string[] }[];
}

export function useMatchPlan(eventId: string | null) {
  return useQuery({
    queryKey: ['match-plan', getActiveOrg(), eventId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(eventId),
    queryFn: () => api.get<MatchPlanRecord>(`/matches/${eventId}/plan`),
  });
}

export function useSaveMatchPlan(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchPlanInput) => api.put(`/matches/${eventId}/plan`, input),
    onSuccess: () => {
      invalidateMatch(qc);
      qc.invalidateQueries({ queryKey: ['match-plan', getActiveOrg()] });
    },
  });
}

export interface MatchAttendanceRecord {
  players: {
    playerId: string;
    firstName: string;
    lastName: string;
    shirtNumber: number | null;
    role: string | null;
    calledUp: boolean;
    status: string | null;
    note: string | null;
  }[];
}

export function useMatchAttendance(eventId: string | null) {
  return useQuery({
    queryKey: ['match-attendance', getActiveOrg(), eventId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(eventId),
    queryFn: () => api.get<MatchAttendanceRecord>(`/matches/${eventId}/attendance`),
  });
}

export function useSaveMatchAttendance(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchAttendanceInput) => api.put(`/matches/${eventId}/attendance`, input),
    onSuccess: () => {
      invalidateMatch(qc);
      qc.invalidateQueries({ queryKey: ['match-attendance', getActiveOrg()] });
    },
  });
}

export interface PlatformOverview {
  since: number;
  totals: {
    clubs: number; users: number; paidClubs: number; freeClubs: number;
    signupsRecent: number; clubsRecent: number; settledCents: number;
    settledCurrency: string | null;
    settledBy: Array<{ currency: string; cents: number; payments: number }>;
    payments: number; creditsOutstanding: number;
    aiCostUsdRecent: number; aiCostUsdTotal: number;
  };
  clubs: Array<{
    orgId: string; name: string; createdAt: number; countryCode: string | null;
    plan: string; storedPlan: string; status: string | null; interval: string | null;
    currentPeriodEnd: number | null; grantedPlan: string | null; grantedUntil: number | null;
    credits: number; creditsGranted: number; creditsSpent: number;
    aiCalls: number; aiCostUsd: number; aiCostUsdRecent: number;
    users: Array<{ id: string; email: string; name: string; createdAt: number; lastLoginAt: number | null }>;
  }>;
}

export interface ClubDetail {
  balance: number;
  ledger: Array<{ id: string; delta: number; reason: string; note: string | null; reference: string | null; createdAt: number }>;
  usage: Array<{ id: string; feature: string; model: string; costUsd: number; credits: number; outcome: string; createdAt: number }>;
  events: Array<{ id: string; type: string; amountCents: number | null; currency: string | null; outcome: string; error: string | null; receivedAt: number }>;
}

export function usePlatformOverview(days = 30, enabled = true) {
  return useQuery({
    queryKey: ['admin-overview', days],
    enabled,
    queryFn: () => api.get<PlatformOverview>(`/admin/overview?days=${days}`),
  });
}

export function useClubDetail(orgId: string | null) {
  return useQuery({
    queryKey: ['admin-club', orgId ?? ''],
    enabled: Boolean(orgId),
    queryFn: () => api.get<ClubDetail>(`/admin/clubs/${orgId}`),
  });
}

export function useGrantPlan(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { plan: 'free' | 'pro' | null; until: string | null; reason: string }) =>
      api.put(`/admin/clubs/${orgId}/plan`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      qc.invalidateQueries({ queryKey: ['admin-club', orgId] });
    },
  });
}

export function useAdjustCredits(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { delta: number; reason: string }) =>
      api.post<{ ok: boolean; balance: number }>(`/admin/clubs/${orgId}/credits`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      qc.invalidateQueries({ queryKey: ['admin-club', orgId] });
    },
  });
}

export interface SellableProduct {
  id: string;
  kind: 'plan' | 'credits';
  label: string;
  priceUsd: number;
  credits?: number;
}

/** Starts a purchase and hands back the Polar URL to send the coach to. */
export function useCheckout() {
  return useMutation({
    mutationFn: (input: { productId: string; returnPath?: string }) =>
      api.post<{ url: string; id: string }>('/billing/checkout', input),
  });
}

export function useBilling() {
  return useQuery({
    queryKey: ['billing', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api.get<{
        plan: string; credits: number; status: string | null; interval: string | null;
        currentPeriodEnd: number | null; cancelAtPeriodEnd: boolean;
        limits: { maxTeams: number; maxPlayersPerTeam: number; ai: boolean; monthlyCredits: number };
        products: SellableProduct[];
      }>('/billing'),
  });
}

export function useSaveMatchAnalysis(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchAnalysisInput) => api.put(`/matches/${eventId}/analysis`, input),
    onSuccess: () => invalidateMatch(qc),
  });
}

export function useAddProblem(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchProblemInput) => api.post(`/matches/${eventId}/problems`, input),
    onSuccess: () => invalidateMatch(qc),
  });
}

export function useDeleteProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (problemId: string) => api.del(`/problems/${problemId}`),
    onSuccess: () => invalidateMatch(qc),
  });
}

export function usePromoteProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ problemId, input }: { problemId: string; input: PromoteProblemInput }) =>
      api.post<{ objectiveId: string; created: boolean }>(
        `/problems/${problemId}/promote`,
        input,
      ),
    onSuccess: () => invalidateMatch(qc),
  });
}

export function useMatchCandidates(teamId?: string) {
  return useQuery({
    queryKey: ['match-candidates', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: async () =>
      (
        await api.get<{ players: RegisterRow[] }>(
          `/match-squad-candidates?teamId=${encodeURIComponent(teamId!)}`,
        )
      ).players,
  });
}

export function useObjectives(teamId?: string, status?: string) {
  return useQuery({
    queryKey: ['objectives', getActiveOrg(), teamId ?? '', status ?? ''],
    enabled: Boolean(getActiveOrg()),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      if (status) params.set('status', status);
      const q = params.toString();
      return (await api.get<{ objectives: ObjectiveRecord[] }>(`/objectives${q ? `?${q}` : ''}`))
        .objectives;
    },
  });
}

export function useCreateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ObjectiveInput) => api.post<{ id: string }>('/objectives', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['objectives', getActiveOrg()] }),
  });
}

export function useUpdateObjectiveStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ objectiveId, status }: { objectiveId: string; status: string }) =>
      api.put(`/objectives/${objectiveId}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['objectives', getActiveOrg()] }),
  });
}

export interface ObjectiveChain {
  chain: ObjectiveRecord[];
  children: ObjectiveRecord[];
  source: {
    kind: string;
    problem?: { title: string; detail: string | null; eventId: string };
    event?: { id: string; onDate: string; opponent: string | null; venue: string | null };
  } | null;
}

export function useObjectiveChain(objectiveId: string | null) {
  return useQuery({
    queryKey: ['objective-chain', getActiveOrg(), objectiveId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(objectiveId),
    queryFn: () => api.get<ObjectiveChain>(`/objectives/${objectiveId}/chain`),
  });
}

// ── Formations ─────────────────────────────────────────────────────────

import type { PlayPhase } from '@shared/schemas';

export interface FormationPlanRow {
  id: string;
  teamId: string;
  name: string;
  formationKey: string;
  notes: string | null;
}

export interface PlacementRow {
  id: string;
  phase: string;
  playerId: string | null;
  positionKey: string | null;
  label: string | null;
  x: number;
  y: number;
  note: string | null;
  sort: number;
}

export interface FormationSquadRow {
  playerId: string;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  shirtNumber: number | null;
  statusKey: string | null;
  roleKey: string | null;
  positionKey: string | null;
}

export function useFormationPlans(teamId?: string) {
  return useQuery({
    queryKey: ['formations', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()),
    queryFn: async () =>
      (
        await api.get<{ plans: FormationPlanRow[] }>(
          teamId ? `/formations?teamId=${encodeURIComponent(teamId)}` : '/formations',
        )
      ).plans,
  });
}

export function useFormationPlan(planId: string | null) {
  return useQuery({
    queryKey: ['formation', getActiveOrg(), planId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(planId),
    queryFn: () =>
      api.get<{
        plan: FormationPlanRow;
        placements: PlacementRow[];
        squad: FormationSquadRow[];
      }>(`/formations/${planId}`),
  });
}

function invalidateFormations(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['formations', getActiveOrg()] });
  qc.invalidateQueries({ queryKey: ['formation', getActiveOrg()] });
}

export function useCreateFormation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { teamId: string; name: string; formationKey: string; notes?: string }) =>
      api.post<{ id: string }>('/formations', input),
    onSuccess: () => invalidateFormations(qc),
  });
}

export function useUpdateFormation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      planId,
      input,
    }: {
      planId: string;
      input: { name: string; formationKey: string; notes?: string; reshape?: boolean };
    }) => api.put(`/formations/${planId}`, input),
    onSuccess: () => invalidateFormations(qc),
  });
}

export function useSavePhase(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phase,
      placements,
    }: {
      phase: PlayPhase;
      placements: Array<Omit<PlacementRow, 'id' | 'phase'>>;
    }) => api.put(`/formations/${planId}/phases/${phase}`, { placements }),
    onSuccess: () => invalidateFormations(qc),
  });
}

export function useCopyPhase(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ from, to }: { from: PlayPhase; to: PlayPhase[] }) =>
      api.post(`/formations/${planId}/copy`, { from, to }),
    onSuccess: () => invalidateFormations(qc),
  });
}

export function useDeleteFormation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => api.del(`/formations/${planId}`),
    onSuccess: () => invalidateFormations(qc),
  });
}

// ── Development ────────────────────────────────────────────────────────

export interface CriterionRow {
  id: string;
  key: string;
  label: string;
  i18nKey: string | null;
  dimension: string;
  sort: number;
}

export interface ScaleRow {
  id: string;
  key: string;
  label: string;
  i18nKey: string | null;
  min: number;
  max: number;
  labels: string[] | null;
  isDefault: boolean;
}

export interface TestRow {
  id: string;
  key: string;
  label: string;
  i18nKey: string | null;
  dimension: string;
  unit: string | null;
  direction: 'higher' | 'lower';
  minValue: number | null;
  maxValue: number | null;
  sort: number;
}

export interface EvaluationListRow {
  id: string;
  playerId: string;
  teamId: string | null;
  onDate: string;
  context: string | null;
  summary: string | null;
  seniorReadiness: string | null;
  scaleKey: string | null;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  averages: Record<string, number>;
  completeness: number;
  criteriaScored: number;
}

export interface ScoreRow {
  id: string;
  criterionKey: string;
  dimension: string;
  score: number | null;
  note: string | null;
}

export interface EvaluationRecord {
  id: string;
  playerId: string;
  teamId: string | null;
  seasonId: string | null;
  onDate: string;
  scaleKey: string | null;
  context: string | null;
  strengths: string | null;
  weaknesses: string | null;
  summary: string | null;
  seniorReadiness: string | null;
  seniorReadinessNote: string | null;
}

export interface EvaluationInputBody {
  playerId: string;
  teamId?: string | null;
  seasonId?: string | null;
  onDate: string;
  scaleKey?: string | null;
  context?: string | null;
  strengths?: string | null;
  weaknesses?: string | null;
  summary?: string | null;
  seniorReadiness?: string | null;
  seniorReadinessNote?: string | null;
  scores: Array<{
    criterionKey: string;
    dimension: string;
    score?: number | null;
    note?: string | null;
  }>;
}

export function useDevelopmentConfig() {
  return useQuery({
    queryKey: ['development-config', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.get<{ criteria: CriterionRow[]; scales: ScaleRow[]; tests: TestRow[] }>(
        '/development/config',
      ),
  });
}

export function useEvaluations(filter: { teamId?: string; playerId?: string }) {
  const query = new URLSearchParams();
  if (filter.teamId) query.set('teamId', filter.teamId);
  if (filter.playerId) query.set('playerId', filter.playerId);
  const qs = query.toString();
  return useQuery({
    queryKey: ['evaluations', getActiveOrg(), qs],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{ evaluations: EvaluationListRow[] }>(
          qs ? `/development/evaluations?${qs}` : '/development/evaluations',
        )
        .then((r) => r.evaluations),
  });
}

export function useEvaluation(id: string | null) {
  return useQuery({
    queryKey: ['evaluation', getActiveOrg(), id ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(id),
    queryFn: () =>
      api.get<{
        evaluation: EvaluationRecord;
        player: { id: string; firstName: string; lastName: string; knownAs: string | null; dateOfBirth: string | null; positionKey: string | null } | null;
        scores: ScoreRow[];
        averages: Record<string, number>;
        completeness: number;
      }>(`/development/evaluations/${id}`),
  });
}

function invalidateDevelopment(qc: ReturnType<typeof useQueryClient>) {
  const org = getActiveOrg();
  for (const key of ['evaluations', 'evaluation', 'player-development', 'batteries', 'battery']) {
    qc.invalidateQueries({ queryKey: [key, org] });
  }
}

export function useCreateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EvaluationInputBody) =>
      api.post<{ id: string }>('/development/evaluations', input),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export function useUpdateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<EvaluationInputBody, 'playerId'> }) =>
      api.put(`/development/evaluations/${id}`, input),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export function useDeleteEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/development/evaluations/${id}`),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export interface BatteryRow {
  id: string;
  teamId: string | null;
  seasonId: string | null;
  name: string;
  onDate: string;
  conditions: string | null;
  notes: string | null;
  /** The tests this day is made of. Null on a day recorded before it was stored. */
  testKeys: string[] | null;
  playersTested: number;
}

export interface ResultRow {
  id: string;
  playerId: string;
  testKey: string;
  value: number | null;
  note: string | null;
  previous: number | null;
  progress: { delta: number; percent: number; improved: boolean } | null;
}

export interface DevelopmentSquadRow {
  playerId: string;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  dateOfBirth: string | null;
  shirtNumber: number | null;
  roleKey: string | null;
  statusKey: string | null;
  positionKey: string | null;
  secondaryPositionKey: string | null;
}

export function useBatteries(teamId?: string) {
  return useQuery({
    queryKey: ['batteries', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{ batteries: BatteryRow[] }>(
          teamId
            ? `/development/batteries?teamId=${encodeURIComponent(teamId)}`
            : '/development/batteries',
        )
        .then((r) => r.batteries),
  });
}

export function useBattery(id: string | null) {
  return useQuery({
    queryKey: ['battery', getActiveOrg(), id ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(id),
    queryFn: () =>
      api.get<{
        battery: BatteryRow;
        tests: TestRow[];
        squad: DevelopmentSquadRow[];
        results: ResultRow[];
      }>(`/development/batteries/${id}`),
  });
}

// ── The test catalogue ─────────────────────────────────────────────────

export interface CatalogueTest extends TestRow {
  isActive: boolean;
  /** Came from the seed, so its label is translated and its key is reserved. */
  seeded: boolean;
  /** Has results behind it, so it can be retired but never deleted. */
  hasResults: boolean;
}

export interface TestInput {
  label: string;
  dimension: string;
  unit: string;
  direction: 'higher' | 'lower';
  minValue?: number | null;
  maxValue?: number | null;
}

/** Every test the club has, retired ones included. */
export function useTests() {
  return useQuery({
    queryKey: ['tests', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () => api.get<{ tests: CatalogueTest[] }>('/development/tests').then((r) => r.tests),
  });
}

/**
 * Anything that changes the catalogue invalidates the development config too —
 * the testing grid reads its columns from there, and a test retired in one tab
 * must not still be offered in another.
 */
function invalidateTests(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['tests'] });
  invalidateDevelopment(qc);
}

export function useCreateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TestInput) =>
      api.post<{ id: string; key: string }>('/development/tests', input),
    onSuccess: () => invalidateTests(qc),
  });
}

export function useUpdateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TestInput }) =>
      api.put<{ renamed: boolean }>(`/development/tests/${id}`, input),
    onSuccess: () => invalidateTests(qc),
  });
}

export function useSetTestActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.put(`/development/tests/${id}/active`, { isActive }),
    onSuccess: () => invalidateTests(qc),
  });
}

export function useDeleteTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/development/tests/${id}`),
    onSuccess: () => invalidateTests(qc),
  });
}

export function useReorderTests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.put<{ ordered: number }>('/development/tests/order', { ids }),
    onSuccess: () => invalidateTests(qc),
  });
}

export function useCreateBattery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      teamId: string;
      name: string;
      onDate: string;
      seasonId?: string | null;
      conditions?: string | null;
      notes?: string | null;
    }) => api.post<{ id: string }>('/development/batteries', input),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export function useUpdateBattery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: {
        name: string;
        onDate: string;
        seasonId?: string | null;
        conditions?: string | null;
        notes?: string | null;
      };
    }) => api.put(`/development/batteries/${id}`, input),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export function useSaveResults(batteryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      results: Array<{ playerId: string; testKey: string; value?: number | null; note?: string | null }>,
    ) =>
      api.put<{ saved: number; rejected: Array<{ playerId: string; testKey: string }> }>(
        `/development/batteries/${batteryId}/results`,
        { results },
      ),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

/**
 * Which tests the day is made of, saved as the coach taps them.
 *
 * Not folded into the battery form: the tests are picked from the grid, where
 * the day's name and date are not on screen, and a save that carried those
 * fields would have to invent them.
 */
export function useSetBatteryTests(batteryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (testKeys: string[]) =>
      api.put<{ testKeys: string[] }>(`/development/batteries/${batteryId}/tests`, { testKeys }),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export function useDeleteBattery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/development/batteries/${id}`),
    onSuccess: () => invalidateDevelopment(qc),
  });
}

export interface PlayerDevelopment {
  player: {
    id: string;
    firstName: string;
    lastName: string;
    knownAs: string | null;
    dateOfBirth: string | null;
    positionKey: string | null;
    secondaryPositionKey: string | null;
    strongFoot: string | null;
  } | null;
  evaluations: Array<{
    id: string;
    onDate: string;
    context: string | null;
    summary: string | null;
    strengths: string | null;
    weaknesses: string | null;
    seniorReadiness: string | null;
    seniorReadinessNote: string | null;
    averages: Record<string, number>;
    completeness: number;
    scores: ScoreRow[];
  }>;
  readinessSteps: Array<{ onDate: string; change: number; to: string | null }>;
  latestReadiness: string | null;
  testing: Array<{
    test: TestRow;
    values: Array<{ onDate: string; value: number }>;
    latest: { onDate: string; value: number };
    trend: { trend: 'improving' | 'stable' | 'declining' | null; samples: number; change: number };
    sinceFirst: { delta: number; percent: number; improved: boolean } | null;
  }>;
}

export function usePlayerDevelopment(playerId: string | null) {
  return useQuery({
    queryKey: ['player-development', getActiveOrg(), playerId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(playerId),
    queryFn: () => api.get<PlayerDevelopment>(`/development/players/${playerId}`),
  });
}

export interface DepthSlotRow {
  id: string;
  positionKey: string;
  label: string | null;
  assessment: string | null;
  note: string | null;
  sort: number;
  entries: Array<{ id: string; playerId: string; rank: number; note: string | null }>;
  suggestion: { suggestion: string; reason: string; cover: number; players: number };
}

export interface DepthChartResponse {
  chart: { id: string; teamId: string; formationKey: string; notes: string | null } | null;
  slots: DepthSlotRow[];
  squad: DevelopmentSquadRow[];
}

/**
 * How the coach arranged the whole-squad pitch sheet.
 *
 * Its own thing, not the depth chart: the sheet is drawn from the chart and
 * falls back to each player's position, which is a good guess and still a
 * guess. This is what he decided the printed page should say, and changing it
 * leaves Positions exactly as it was.
 */
export interface SquadSheetArrangement {
  formationKey: string | null;
  placements: Record<string, string>;
}

export function useSquadSheet(teamId: string | null) {
  return useQuery({
    queryKey: ['squadSheet', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: () => api.get<SquadSheetArrangement>(`/squad-sheet/${teamId}`),
  });
}

export function useSaveSquadSheet(teamId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { formationKey: string; placements: Record<string, string> }) =>
      api.put<SquadSheetArrangement>(`/squad-sheet/${teamId}`, input),
    onSuccess: (data) => {
      qc.setQueryData(['squadSheet', getActiveOrg(), teamId ?? ''], data);
    },
  });
}

export function useDepthChart(teamId: string | null) {
  return useQuery({
    queryKey: ['depth', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: () => api.get<DepthChartResponse>(`/development/depth/${teamId}`),
  });
}

export function useSaveDepthChart(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      formationKey: string;
      notes?: string | null;
      slots?: Array<{
        positionKey: string;
        label?: string | null;
        assessment?: string | null;
        note?: string | null;
        entries: Array<{ playerId: string; rank: number; note?: string | null }>;
      }>;
    }) => api.put<{ id: string }>(`/development/depth/${teamId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depth', getActiveOrg()] });
    },
  });
}

// ── Trials ─────────────────────────────────────────────────────────────

export interface TrialRow {
  id: string;
  teamId: string | null;
  seasonId: string | null;
  name: string;
  startsOn: string;
  endsOn: string | null;
  location: string | null;
  criteria: string | null;
  status: string;
  notes: string | null;
  candidates: number;
  selected: number;
  sessions: number;
}

export interface CandidateRow {
  id: string;
  trialId: string;
  playerId: string | null;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  currentClub: string | null;
  primaryPositionKey: string | null;
  secondaryPositionKey: string | null;
  strongFoot: string | null;
  heightCm: number | null;
  weightKg: number | null;
  bibNumber: number | null;
  status: string;
  decision: string | null;
  decisionReason: string | null;
  decidedOn: string | null;
  notes: string | null;
  summary: {
    observations: number;
    attended: number;
    missed: number;
    averages: Record<string, number>;
    overall: number | null;
    spread: number | null;
    basis: string;
    recommendations: Record<string, number>;
  };
  standing: { standing: string; reason: string; signals: string[] };
}

export interface TrialSessionRow {
  id: string;
  trialId: string;
  name: string;
  onDate: string;
  startsAt: string | null;
  focus: string | null;
  notes: string | null;
}

export interface ObservationRow {
  id: string;
  sessionId: string;
  candidateId: string;
  attendance: string;
  positionPlayed: string | null;
  technicalScore: number | null;
  tacticalScore: number | null;
  physicalScore: number | null;
  mentalScore: number | null;
  disciplineScore: number | null;
  coachabilityScore: number | null;
  note: string | null;
  recommendation: string | null;
}

export interface TrialInputBody {
  teamId?: string | null;
  seasonId?: string | null;
  name: string;
  startsOn: string;
  endsOn?: string | null;
  location?: string | null;
  criteria?: string | null;
  status?: string;
  notes?: string | null;
}

export function useTrials(teamId?: string) {
  return useQuery({
    queryKey: ['trials', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{ trials: TrialRow[] }>(
          teamId ? `/trials?teamId=${encodeURIComponent(teamId)}` : '/trials',
        )
        .then((r) => r.trials),
  });
}

export function useTrial(trialId: string | null) {
  return useQuery({
    queryKey: ['trial', getActiveOrg(), trialId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(trialId),
    queryFn: () =>
      api.get<{
        trial: TrialRow;
        candidates: CandidateRow[];
        sessions: TrialSessionRow[];
        observations: ObservationRow[];
      }>(`/trials/${trialId}`),
  });
}

function invalidateTrials(qc: ReturnType<typeof useQueryClient>) {
  const org = getActiveOrg();
  qc.invalidateQueries({ queryKey: ['trials', org] });
  qc.invalidateQueries({ queryKey: ['trial', org] });
  qc.invalidateQueries({ queryKey: ['players', org] });
}

export function useCreateTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TrialInputBody) => api.post<{ id: string }>('/trials', input),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useUpdateTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<TrialInputBody, 'teamId'> }) =>
      api.put(`/trials/${id}`, input),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useDeleteTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/trials/${id}`),
    onSuccess: () => invalidateTrials(qc),
  });
}

export interface CandidateInputBody {
  firstName: string;
  lastName: string;
  knownAs?: string | null;
  dateOfBirth?: string | null;
  currentClub?: string | null;
  primaryPositionKey?: string | null;
  strongFoot?: string | null;
  bibNumber?: number | null;
  status?: string;
  decision?: string | null;
  decisionReason?: string | null;
  decidedOn?: string | null;
  notes?: string | null;
}

export function useAddCandidates(trialId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (candidates: CandidateInputBody[]) =>
      api.post<{ ids: string[] }>(`/trials/${trialId}/candidates`, { candidates }),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CandidateInputBody }) =>
      api.put(`/trials/candidates/${id}`, input),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useDeleteCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/trials/candidates/${id}`),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useConvertCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: { teamId?: string | null; shirtNumber?: number | null; roleKey?: string | null };
    }) => api.post<{ playerId: string; teamId: string | null }>(`/trials/candidates/${id}/convert`, input),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useCreateTrialSession(trialId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      onDate: string;
      startsAt?: string | null;
      focus?: string | null;
      notes?: string | null;
    }) => api.post<{ id: string }>(`/trials/${trialId}/sessions`, input),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useDeleteTrialSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.del(`/trials/sessions/${sessionId}`),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useSaveObservations(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (observations: Array<Omit<ObservationRow, 'id' | 'sessionId'>>) =>
      api.put<{ saved: number }>(`/trials/sessions/${sessionId}/observations`, { observations }),
    onSuccess: () => invalidateTrials(qc),
  });
}

export function useTrialHistory(playerId: string | null) {
  return useQuery({
    queryKey: ['trial-history', getActiveOrg(), playerId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(playerId),
    queryFn: () =>
      api
        .get<{
          trials: Array<{
            candidateId: string;
            trialId: string;
            trialName: string;
            startsOn: string;
            decision: string | null;
            decisionReason: string | null;
            decidedOn: string | null;
          }>;
        }>(`/trials/players/${playerId}/history`)
        .then((r) => r.trials),
  });
}

// ── Goalkeepers and a club's own fields ────────────────────────────────

export interface GoalkeeperProfileRow {
  id: string;
  playerId: string;
  standingReachCm: number | null;
  wingspanCm: number | null;
  distributionFoot: string | null;
  dominantHand: string | null;
  profileNote: string | null;
}

export function useGoalkeeperProfile(playerId: string | null) {
  return useQuery({
    queryKey: ['goalkeeper', getActiveOrg(), playerId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(playerId),
    queryFn: () =>
      api
        .get<{ profile: GoalkeeperProfileRow | null }>(`/players/${playerId}/goalkeeper`)
        .then((r) => r.profile),
  });
}

export function useSaveGoalkeeperProfile(playerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      standingReachCm?: number | null;
      wingspanCm?: number | null;
      distributionFoot?: string | null;
      dominantHand?: string | null;
      profileNote?: string | null;
    }) => api.put(`/players/${playerId}/goalkeeper`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goalkeeper', getActiveOrg()] });
    },
  });
}

export interface PlayerFieldRow {
  id: string;
  key: string;
  label: string;
  kind: string;
  isSensitive: boolean;
  source: string;
  sort: number;
}

export function usePlayerFields() {
  return useQuery({
    queryKey: ['player-fields', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.get<{ fields: PlayerFieldRow[] }>('/player-fields').then((r) => r.fields),
  });
}

export function useCreatePlayerField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; kind?: string; isSensitive?: boolean }) =>
      api.post<{ fields: PlayerFieldRow[] }>('/player-fields', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['player-fields', getActiveOrg()] });
    },
  });
}

export function useRetirePlayerField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => api.del(`/player-fields/${fieldId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['player-fields', getActiveOrg()] });
    },
  });
}

export function useSaveAttributes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      playerId,
      attributes,
    }: {
      playerId: string;
      attributes: Record<string, string | null>;
    }) => api.put<{ saved: number }>(`/players/${playerId}/attributes`, { attributes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players', getActiveOrg()] });
    },
  });
}

// ── The club, and its branding ─────────────────────────────────────────

export interface OrgRow {
  id: string;
  name: string;
  locale: string;
  timezone: string;
  countryCode: string | null;
  logoKey: string | null;
  primaryColor: string | null;
  reportFooter: string | null;
}

export function useOrg() {
  return useQuery({
    queryKey: ['org', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () => api.get<{ org: OrgRow }>('/org').then((r) => r.org),
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      locale?: string;
      timezone?: string;
      countryCode?: string | null;
      primaryColor?: string | null;
      reportFooter?: string | null;
    }) => api.put('/org', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', getActiveOrg()] });
    },
  });
}

/** The crest goes to R2 as raw bytes; there is no form encoding to negotiate. */
export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await fetch('/api/org/logo', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': file.type, 'X-Org-Id': getActiveOrg() ?? '' },
        body: file,
      });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as { logoKey: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', getActiveOrg()] });
    },
  });
}

export function useRemoveLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del('/org/logo'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', getActiveOrg()] });
    },
  });
}

// ── Decision log ───────────────────────────────────────────────────────

export interface DecisionRow {
  id: string;
  teamId: string | null;
  seasonId: string | null;
  onDate: string;
  category: string;
  decision: string;
  reason: string;
  subjectKind: string | null;
  subjectId: string | null;
  subjectLabel: string | null;
  outcome: string | null;
  outcomeOn: string | null;
  outcomeVerdict: string | null;
}

export interface DecisionInputBody {
  teamId?: string | null;
  seasonId?: string | null;
  onDate: string;
  category?: string;
  decision: string;
  reason: string;
  subjectKind?: string | null;
  subjectId?: string | null;
  subjectLabel?: string | null;
  outcome?: string | null;
  outcomeOn?: string | null;
  outcomeVerdict?: string | null;
}

export function useDecisions(filter: {
  teamId?: string;
  subjectKind?: string;
  subjectId?: string;
} = {}) {
  const query = new URLSearchParams();
  if (filter.teamId) query.set('teamId', filter.teamId);
  if (filter.subjectKind) query.set('subjectKind', filter.subjectKind);
  if (filter.subjectId) query.set('subjectId', filter.subjectId);
  const qs = query.toString();
  return useQuery({
    queryKey: ['decisions', getActiveOrg(), qs],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{ decisions: DecisionRow[] }>(qs ? `/decisions?${qs}` : '/decisions')
        .then((r) => r.decisions),
  });
}

/** Decisions old enough to be judged, with no outcome recorded. */
export function useDecisionsAwaitingReview(days = 14) {
  return useQuery({
    queryKey: ['decisions-review', getActiveOrg(), days],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{ decisions: DecisionRow[] }>(`/decisions/review?days=${days}`)
        .then((r) => r.decisions),
  });
}

export function useDecisionSummary(teamId?: string) {
  return useQuery({
    queryKey: ['decision-summary', getActiveOrg(), teamId ?? ''],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api.get<{
        total: number;
        reviewed: number;
        awaiting: number;
        byCategory: Record<string, number>;
        byVerdict: Record<string, number>;
      }>(teamId ? `/decisions/summary?teamId=${encodeURIComponent(teamId)}` : '/decisions/summary'),
  });
}

function invalidateDecisions(qc: ReturnType<typeof useQueryClient>) {
  const org = getActiveOrg();
  for (const key of ['decisions', 'decisions-review', 'decision-summary']) {
    qc.invalidateQueries({ queryKey: [key, org] });
  }
}

export function useCreateDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DecisionInputBody) => api.post<{ id: string }>('/decisions', input),
    onSuccess: () => invalidateDecisions(qc),
  });
}

export function useUpdateDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DecisionInputBody }) =>
      api.put(`/decisions/${id}`, input),
    onSuccess: () => invalidateDecisions(qc),
  });
}

export function useRecordOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: { outcome: string; outcomeVerdict?: string | null; outcomeOn?: string | null };
    }) => api.put(`/decisions/${id}/outcome`, input),
    onSuccess: () => invalidateDecisions(qc),
  });
}

export function useDeleteDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/decisions/${id}`),
    onSuccess: () => invalidateDecisions(qc),
  });
}

/**
 * Re-reads stored positions against the club's configuration.
 *
 * For squads imported before positions were resolved on the way in: a list that
 * said "Gardien" stored that word, and every outfield player showed no position.
 */
export function useResolvePositions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId?: string) =>
      api.post<{ scanned: number; fixed: number; unresolved: string[] }>(
        teamId ? `/players/resolve-positions?teamId=${encodeURIComponent(teamId)}` : '/players/resolve-positions',
        {},
      ),
    onSuccess: () => invalidatePlayers(qc),
  });
}

// ── Bulk edits ─────────────────────────────────────────────────────────

/**
 * One patch applied to a selection of players.
 *
 * The response names what failed rather than reporting success for a batch that
 * half worked — a shirt clash or a missing player must be visible.
 */
export function useBulkUpdatePlayers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      playerIds,
      patch,
    }: {
      playerIds: string[];
      patch: Omit<PlayerInput, 'firstName' | 'lastName' | 'shirtNumber'> & {
        firstName?: string;
        lastName?: string;
      };
    }) =>
      api.patch<{ updated: number; failed: { playerId: string; error: string }[] }>(
        '/players',
        { playerIds, patch },
      ),
    onSuccess: () => invalidatePlayers(qc),
  });
}

export function useBulkArchivePlayers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ playerIds, archived }: { playerIds: string[]; archived: boolean }) =>
      api.post<{ updated: number }>('/players/archive', { playerIds, archived }),
    onSuccess: () => invalidatePlayers(qc),
  });
}

/** One decision applied to several trial candidates. The reason is shared. */
export function useBulkUpdateCandidates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      candidateIds: string[];
      status?: string;
      decision?: string | null;
      decisionReason?: string | null;
    }) => api.patch<{ updated: number }>('/trials/candidates', input),
    onSuccess: () => invalidateTrials(qc),
  });
}

// ── The paper register ─────────────────────────────────────────────────

export interface RegisterSquadRow {
  id: string;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  shirtNumber: number | null;
  positionKey: string | null;
}

export interface RegisterResponse {
  squad: RegisterSquadRow[];
  sessions: { id: string; onDate: string; title: string | null }[];
  /** Fixtures in the period. A match day is an attendance day too. */
  matches: { id: string; onDate: string; opponent: string | null }[];
  recorded: { playerId: string; date: string; status: string; minutesDone: number | null }[];
}

export function useRegister(teamId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: ['register', getActiveOrg(), teamId ?? '', from, to],
    enabled: Boolean(getActiveOrg()) && Boolean(teamId),
    queryFn: () =>
      api.get<RegisterResponse>(
        `/register/${teamId}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  });
}

/**
 * Writes a reviewed register. Replaces rather than merges: the coach has just
 * looked at the grid and said it is right.
 */
export function useSaveRegister(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      cells: { playerId: string; date: string; status: string; minutesDone?: number | null }[];
      createMissingSessions: boolean;
      sessionTitle: string;
    }) =>
      api.put<{ saved: number; sessionsCreated: number; skippedDates: string[] }>(
        `/register/${teamId}`,
        input,
      ),
    onSuccess: () => {
      const org = getActiveOrg();
      for (const key of ['register', 'sessions', 'load', 'players']) {
        qc.invalidateQueries({ queryKey: [key, org] });
      }
    },
  });
}

export function usePlayerAttendance(playerId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: ['player-attendance', getActiveOrg(), playerId ?? '', from, to],
    enabled: Boolean(getActiveOrg()) && Boolean(playerId),
    queryFn: () =>
      api.get<{
        rows: { date: string; title: string | null; status: string; minutesDone: number | null }[];
        counts: Record<string, number>;
        sessions: number;
        attended: number;
        rate: number | null;
      }>(`/register/players/${playerId}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });
}

/** Which other positions a player in one can also fill. The club's own call. */
export function useSetPositionCovers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ positionKey, covers }: { positionKey: string; covers: string[] }) =>
      api.put(`/config/positions/${encodeURIComponent(positionKey)}/covers`, { covers }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', getActiveOrg()] });
    },
  });
}

// ── Blocks: macrocycles and mesocycles ─────────────────────────────────

export interface CycleObjective {
  id: string;
  dimension: string | null;
  title: string;
  detail: string | null;
  status: string;
}

export interface CycleRow {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  dominantTheme: string | null;
  secondaryTheme: string | null;
  objectiveSummary: string | null;
  diagnosis: string | null;
  evidence: string | null;
  rationale: string | null;
  principleIds: string[];
  physicalEmphasis: string[];
  successCriteria: string | null;
  evaluationMethod: string | null;
  modifyIf: string | null;
  reviewOn: string | null;
  outcome: string | null;
  sort: number;
  objectives: CycleObjective[];
}

export interface MacrocycleRow extends CycleRow {
  seasonId: string;
  mesocycles: (CycleRow & { macrocycleId: string })[];
}

export function useCycles(seasonId: string | null) {
  return useQuery({
    queryKey: ['cycles', getActiveOrg(), seasonId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(seasonId),
    queryFn: () =>
      api
        .get<{ macrocycles: MacrocycleRow[] }>(`/cycles?seasonId=${seasonId}`)
        .then((r) => r.macrocycles),
  });
}

export interface CycleInputBody {
  name: string;
  startsOn: string;
  endsOn: string;
  dominantTheme?: string;
  secondaryTheme?: string;
  objectiveSummary?: string;
  diagnosis?: string;
  evidence?: string;
  rationale?: string;
  principleIds?: string[];
  physicalEmphasis?: string[];
  successCriteria?: string;
  evaluationMethod?: string;
  modifyIf?: string;
  reviewOn?: string;
  outcome?: string;
  objectives?: { dimension: string; title: string; detail?: string }[];
  sort?: number;
}

function invalidateCycles(qc: ReturnType<typeof useQueryClient>) {
  const org = getActiveOrg();
  for (const key of ['cycles', 'blocks-review', 'objectives', 'microcycles']) {
    qc.invalidateQueries({ queryKey: [key, org] });
  }
}

export function useSaveCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      cycleId,
      input,
    }: {
      kind: 'macrocycle' | 'mesocycle';
      cycleId?: string;
      input: CycleInputBody & { seasonId?: string; macrocycleId?: string };
    }) => {
      const path = kind === 'macrocycle' ? '/macrocycles' : '/mesocycles';
      return cycleId
        ? api.put(`${path}/${cycleId}`, input)
        : api.post<{ id: string }>(path, input);
    },
    onSuccess: () => invalidateCycles(qc),
  });
}

export function useDeleteCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, cycleId }: { kind: 'macrocycle' | 'mesocycle'; cycleId: string }) =>
      api.del(`${kind === 'macrocycle' ? '/macrocycles' : '/mesocycles'}/${cycleId}`),
    onSuccess: () => invalidateCycles(qc),
  });
}

/** Blocks whose review date has arrived with no outcome recorded. */
export function useBlocksDueReview() {
  return useQuery({
    queryKey: ['blocks-review', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () =>
      api
        .get<{
          blocks: {
            id: string;
            name: string;
            startsOn: string;
            endsOn: string;
            reviewOn: string | null;
            modifyIf: string | null;
            successCriteria: string | null;
          }[];
        }>('/blocks/review')
        .then((r) => r.blocks),
  });
}

// ── The assistants ─────────────────────────────────────────────────────

export interface AiGrounding {
  why: string;
  dataUsed: string[];
  uncertainty: string;
}

export interface AiSessionPlan extends AiGrounding {
  title: string;
  objective: string;
  rationale: string;
  exercises: Array<{
    name: string;
    categoryKey: string;
    durationMin: number;
    objective: string;
    rationale: string;
    players?: string;
    areaLength?: number;
    areaWidth?: number;
    workSeconds?: number;
    restSeconds?: number;
    reps?: number;
    sets?: number;
  }>;
  coachingPoints: string[];
  watchFor: string[];
}

export interface AiWeekReview extends AiGrounding {
  headline: string;
  observations: Array<{ text: string; signal: string }>;
  considerations: Array<{ text: string; because: string }>;
}

/** Every assistant answers in the same envelope, so one panel renders any of them. */
export interface AiAnswer<T> {
  result: T;
  model: string;
  credits: number;
  costUsd: number;
  balance: number;
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
  };
}

export interface AiStatus {
  enabled: boolean;
  inPlan: boolean;
  credits: number;
  assistants: Array<{ key: string; model: string }>;
}

export function useAiStatus() {
  return useQuery({
    queryKey: ['ai-status', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () => api.get<AiStatus>('/ai/status'),
  });
}

export function useSessionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      teamId: string;
      onDate: string;
      objective: string;
      durationMin: number;
    }) => api.post<AiAnswer<AiSessionPlan>>('/ai/session-plan', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useWeekReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { teamId: string; from: string; to: string }) =>
      api.post<AiAnswer<AiWeekReview>>('/ai/week-review', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export interface AiMatchObjectives extends AiGrounding {
  problems: Array<{
    title: string;
    detail: string;
    phase: string;
    dimension: string;
    priority: number;
    proposedObjective: string;
  }>;
}

export interface AiReportNarrative extends AiGrounding {
  paragraphs: string[];
  caveats: string[];
}

export function useMatchObjectives() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { eventId: string }) =>
      api.post<AiAnswer<AiMatchObjectives>>('/ai/match-objectives', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useReportNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      teamId: string;
      kind: string;
      from: string;
      to: string;
      figures: Record<string, string | number | null>;
    }) => api.post<AiAnswer<AiReportNarrative>>('/ai/report-narrative', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export interface AiAdminOverview {
  settings: {
    enabled: boolean;
    orgDailyCapUsd: number;
    platformDailyCapUsd: number;
    models: Record<string, string>;
  };
  since: number;
  spentTodayUsd: number;
  callsToday: number;
  spentUsd: number;
  calls: number;
  failed: number;
  byFeature: Array<{ feature: string; calls: number; costUsd: number; credits: number }>;
}

export function useAiAdmin(days = 30, enabled = true) {
  return useQuery({
    queryKey: ['admin-ai', days],
    enabled,
    queryFn: () => api.get<AiAdminOverview>(`/admin/ai?days=${days}`),
  });
}

export function useSetAiSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: boolean | number | string }) =>
      api.put('/admin/ai/settings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-ai'] });
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
    },
  });
}

// ── The chat ───────────────────────────────────────────────────────────

export interface ChatThread {
  id: string;
  teamId: string | null;
  title: string;
  createdAt: number;
  lastMessageAt: number;
}

export type ChatArtifact =
  | {
      kind: 'session';
      title: string;
      objective: string;
      rationale: string;
      durationMin: number;
      exercises: Array<{
        name: string;
        categoryKey: string;
        durationMin: number;
        objective: string;
        rationale: string;
      }>;
    }
  | {
      kind: 'week';
      name: string;
      objectiveSummary: string;
      days: Array<{
        mdLabel: string;
        mainObjective: string;
        dayType: 'rest' | 'recovery' | 'normal' | 'high';
        plannedDurationMin: number;
      }>;
    }
  | {
      kind: 'objective';
      level: 'season' | 'cycle' | 'microcycle' | 'session';
      dimension: 'technical' | 'tactical' | 'physical' | 'mental' | 'social';
      title: string;
      detail: string;
    }
  | {
      kind: 'exercise';
      name: string;
      categoryKey: string;
      durationMin: number;
      rationale: string;
      minPlayers?: number;
      maxPlayers?: number;
      areaLength?: number;
      areaWidth?: number;
      workSeconds?: number;
      restSeconds?: number;
    };

export interface ChatMessage {
  id: string;
  role: string;
  answer: string;
  why: string;
  touchesHealth: boolean;
  artifacts: ChatArtifact[];
  credits: number;
  costUsd: number;
  createdAt: number;
}

export interface ChatReply {
  threadId: string;
  answer: string;
  why: string;
  touchesHealth: boolean;
  artifacts: ChatArtifact[];
  credits: number;
  costUsd: number;
  balance: number;
  messagesLeft: number;
  droppedTurns: number;
  model: string;
}

export function useChatThreads() {
  return useQuery({
    queryKey: ['chat', getActiveOrg()],
    enabled: Boolean(getActiveOrg()),
    queryFn: () => api.get<{ threads: ChatThread[] }>('/ai/chat').then((r) => r.threads),
  });
}

export function useChatThread(threadId: string | null) {
  return useQuery({
    queryKey: ['chat-thread', getActiveOrg(), threadId ?? ''],
    enabled: Boolean(getActiveOrg()) && Boolean(threadId),
    queryFn: () =>
      api.get<{ thread: ChatThread; messages: ChatMessage[] }>(`/ai/chat/${threadId}`),
  });
}

export function useAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { question: string; threadId?: string; teamId?: string | null }) =>
      api.post<ChatReply>('/ai/chat', input),
    onSuccess: (reply) => {
      void qc.invalidateQueries({ queryKey: ['chat'] });
      void qc.invalidateQueries({ queryKey: ['chat-thread', getActiveOrg(), reply.threadId] });
      void qc.invalidateQueries({ queryKey: ['ai-status'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

/**
 * One turn, streamed.
 *
 * The answer is shown as it is written, which is the difference between an
 * assistant that feels alive and one that feels like a form being submitted.
 * `streaming` is the text so far; `reply` is the settled turn, which is what
 * the thread query then refetches and renders properly.
 *
 * It is a hook rather than a mutation because the useful state here is a string
 * that changes forty times a second, and TanStack Query is not for that.
 */
export function useAskStreaming() {
  const qc = useQueryClient();
  const [streaming, setStreaming] = useState('');
  const [pending, setPending] = useState(false);
  const [reply, setReply] = useState<ChatReply | null>(null);
  const [error, setError] = useState<{ code: string } | null>(null);
  const abort = useRef<AbortController | null>(null);

  const ask = useCallback(
    async (input: { question: string; threadId?: string; teamId?: string | null }) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      setPending(true);
      setStreaming('');
      setError(null);

      let settled: ChatReply | null = null;
      let refusal: { code: string } | null = null;
      try {
        await postEventStream('/ai/chat/stream', input, {
          signal: controller.signal,
          onEvent: (event, data) => {
            if (event === 'delta') {
              setStreaming((current) => current + ((data as { text?: string }).text ?? ''));
            } else if (event === 'done') {
              settled = data as ChatReply;
            } else if (event === 'error') {
              refusal = { code: (data as { error?: string }).error ?? 'ai_failed' };
            }
          },
        });
      } catch (e) {
        if (controller.signal.aborted) return null;
        refusal = { code: e instanceof ApiFailure ? e.code : 'ai_failed' };
      } finally {
        if (!controller.signal.aborted) setPending(false);
      }

      if (refusal) {
        setError(refusal);
        setStreaming('');
        return null;
      }
      if (settled) {
        const done = settled as ChatReply;
        setReply(done);
        void qc.invalidateQueries({ queryKey: ['chat'] });
        void qc.invalidateQueries({ queryKey: ['chat-thread', getActiveOrg(), done.threadId] });
        void qc.invalidateQueries({ queryKey: ['ai-status'] });
        void qc.invalidateQueries({ queryKey: ['billing'] });
        // Cleared only once the stored turn is on its way: clearing it here
        // would blank the answer for the moment between the two.
        setStreaming('');
        return done;
      }
      return null;
    },
    [qc],
  );

  /** Stops the answer. The turn is already recorded and charged server-side. */
  const stop = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setPending(false);
    setStreaming('');
  }, []);

  const reset = useCallback(() => {
    setStreaming('');
    setError(null);
    setReply(null);
  }, []);

  return { ask, stop, reset, streaming, pending, reply, error };
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => api.del(`/ai/chat/${threadId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat'] }),
  });
}
